import {
  ChoiceCard,
  FieldLabel,
  ERROR_TEXT,
  Hint,
  SectionCard,
} from '../UIComponents';

import {
  calculateFleetAverages,
  downloadFleetExcelTemplate,
  parseFleetExcelFile,
} from '../../../domain/data-collection/fleetExcel';
import { cleanNumericInput, formatNumericInput, parseNumericInput } from '../../../utils/numberInput';
import { readSdxValue } from '../../upload/Eingabemaske';

const COMPRESSOR_OPTIONS = [
  'Kolbenprozessor',
  'Schraubenkompressor (drehzahlgeregelt)',
  'Schraubenkompressor (ohne Drehzahlregelung)',
  'Weiss nicht',
]

const BRAND = '#004A53';
const INTERACTION = '#026F6D';
const GREEN_SURFACE = '#f0f7f6';
const GREEN_BORDER = '#b7cfcc';
const MUTED = '#64748b';

const OWNERSHIP_OPTIONS = [
  {
    value: 'Eigentum',
    label: 'Eigentum',
    scope: 'typisch Scope 1',
  },

  {
    value: 'Drittfirma(Leasing/Miete)',
    label: 'Drittfirma(Leasing/Miete)',
    scope: 'typisch Scope 3',
  }
];

/*
const WASTE_OPTIONS = [
  {
    value: 'paints',
    label: 'Lacke',
  },
  {
    value: 'solvents',
    label: 'Lösungsmittel',
  },
  {
    value: 'non_repairable_body_parts',
    label:
      'Nicht reparierbare Carrosserieteile',
  },
];
*/

function FleetRow({
  title,
  countField,
  ageField,
  kmField,
  formData,
  setField,
  disabled,
  fieldErrors,
}) {
  const hasVehicles = parseNumericInput(formData[countField]) > 0;
  const rowError =
    fieldErrors?.[countField] ||
    fieldErrors?.[ageField] ||
    fieldErrors?.[kmField];

  return (
    <div style={{ marginBottom: 16 }}>
      <h3
        className="h3"
        style={{
          margin: '0 0 12px',
          fontSize: 14,
          color: BRAND,
        }}
      >
        {title}
      </h3>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns:
            '1fr 1fr 1fr',
          gap: 12,
        }}
      >
        <sdx-input
          label="Anzahl"
          type="text"
          inputmode="decimal"
          value={formatNumericInput(formData[countField] ?? '')}
          onInput={event =>
            setField(
              countField,
              cleanNumericInput(event.target.value)
            )
          }
          disabled={disabled || undefined}
          placeholder="0"
          valid={
            fieldErrors?.[countField]
              ? 'false'
              : undefined
          }
          validation-message={
            fieldErrors?.[countField] ||
            undefined
          }
        ></sdx-input>

        <sdx-input
          label="Ø Alter (Jahre)"
          type="text"
          inputmode="decimal"
          value={hasVehicles ? formatNumericInput(formData[ageField] ?? '') : ''}
          onInput={event =>
            setField(
              ageField,
              cleanNumericInput(event.target.value)
            )
          }
          disabled={disabled || !hasVehicles || undefined}
          placeholder="z. B. 4"
          valid={
            fieldErrors?.[ageField]
              ? 'false'
              : undefined
          }
          validation-message={
            fieldErrors?.[ageField] ||
            undefined
          }
        ></sdx-input>

        <sdx-input
          label="Ø km/Jahr"
          type="text"
          inputmode="decimal"
          value={hasVehicles ? formatNumericInput(formData[kmField] ?? '') : ''}
          onInput={event =>
            setField(
              kmField,
              cleanNumericInput(event.target.value)
            )
          }
          disabled={disabled || !hasVehicles || undefined}
          placeholder="z. B. 15'000"
          valid={
            fieldErrors?.[kmField]
              ? 'false'
              : undefined
          }
          validation-message={
            fieldErrors?.[kmField] ||
            undefined
          }
        ></sdx-input>
      </div>

      {rowError && (
        <span style={ERROR_TEXT}>
          {rowError}
        </span>
      )}
    </div>
  );
}

const RequiredBadge = () => (
  <span
    style={{
      marginLeft: 6,
      fontSize: 11,
      padding: '1px 7px',
      borderRadius: 999,
      background: '#fef3c7',
      color: '#92400e',
      fontWeight: 600,
    }}
  >
    Pflicht
  </span>
);

export default function OtherStep({
  formData,
  setField,
  isReadOnly,
  fieldErrors,
  onFleetFileChange,
}) {
  function toggleWasteCategory(category) {
    const currentCategories =
      Array.isArray(formData.wasteCategories)
        ? formData.wasteCategories
        : [];

    setField(
      'wasteCategories',
      currentCategories.includes(category)
        ? currentCategories.filter(
            item => item !== category
          )
        : [...currentCategories, category]
    );
  }

  const importedVehicles = Array.isArray(
    formData.fleetImportedVehicles
  )
    ? formData.fleetImportedVehicles
    : [];

  const hasFleetImport =
    formData.fleetImportSuccessful === true &&
    importedVehicles.length > 0;

  const manualFieldsDisabled =
    isReadOnly || hasFleetImport;

  async function handleFleetFile(file) {
    if (!file) return;

    setField('fleetImportError', '');
    setField('fleetImportMessage', '');

    try {
      const vehicles =
        await parseFleetExcelFile(file);

      const averages =
        calculateFleetAverages(vehicles);

      setField(
        'fleetImportedVehicles',
        vehicles
      );

      setField(
        'fleetFileName',
        file.name
      );

      setField(
        'combustionCount',
        averages.combustion.count
      );

      setField(
        'combustionAvgAge',
        averages.combustion.averageAge
      );

      setField(
        'combustionAvgKm',
        averages.combustion.averageKm
      );

      setField(
        'electricCount',
        averages.electric.count
      );

      setField(
        'electricAvgAge',
        averages.electric.averageAge
      );

      setField(
        'electricAvgKm',
        averages.electric.averageKm
      );

      setField(
        'hybridCount',
        averages.hybrid.count
      );

      setField(
        'hybridAvgAge',
        averages.hybrid.averageAge
      );

      setField(
        'hybridAvgKm',
        averages.hybrid.averageKm
      );

      if (
        typeof onFleetFileChange ===
        'function'
      ) {
        onFleetFileChange(file);
      }

      setField(
        'fleetImportSuccessful',
        true
      );

      setField(
        'fleetImportMessage',
        `${vehicles.length} Fahrzeug${
          vehicles.length === 1 ? '' : 'e'
        } erfolgreich importiert.`
      );
    } catch (error) {
      setField(
        'fleetImportedVehicles',
        []
      );

      setField('fleetFileName', '');

      if (
        typeof onFleetFileChange ===
        'function'
      ) {
        onFleetFileChange(null);
      }

      setField(
        'fleetImportSuccessful',
        false
      );

      setField(
        'fleetImportMessage',
        ''
      );

      setField(
        'fleetImportError',
        error instanceof Error
          ? error.message
          : 'Die Fahrzeugliste konnte nicht eingelesen werden.'
      );
    }
  }

  function removeFleetImport() {
    setField(
      'fleetImportedVehicles',
      []
    );

    setField('fleetFileName', '');

    setField(
      'combustionCount',
      ''
    );

    setField(
      'combustionAvgAge',
      ''
    );

    setField(
      'combustionAvgKm',
      ''
    );

    setField(
      'electricCount',
      ''
    );

    setField(
      'electricAvgAge',
      ''
    );

    setField(
      'electricAvgKm',
      ''
    );

    setField(
      'hybridCount',
      ''
    );

    setField(
      'hybridAvgAge',
      ''
    );

    setField(
      'hybridAvgKm',
      ''
    );

    if (
      typeof onFleetFileChange ===
      'function'
    ) {
      onFleetFileChange(null);
    }

    setField(
      'fleetImportSuccessful',
      false
    );

    setField(
      'fleetImportMessage',
      ''
    );

    setField('fleetImportError', '');
  }

  const combustion =
    parseFloat(formData.combustionCount) ||
    0;

  const electric =
    parseFloat(formData.electricCount) ||
    0;

  const hybrid =
    parseFloat(formData.hybridCount) || 0;

  const total =
    combustion + electric + hybrid;

  const electrifiedShare =
    total > 0
      ? Math.round(
          ((electric + hybrid) / total) *
            100
        )
      : 0;

  const bodyPartsThirdPartyShare =
    Number(
      formData.bodyPartsThirdPartyShare ??
        0
    );

  const bodyPartsMixedContainerShare =
    100 - bodyPartsThirdPartyShare;

  return (
    <div>
      {/* Fleet */}
      <SectionCard
        title="Ersatzwagenflotte"
        badge="optional"
      >
        <div style={{ marginBottom: 16 }}>
          <label
            className="label"
            style={{
              display: 'block',
              marginBottom: 6,
            }}
          >
            Fahrzeugliste hochladen
          </label>

          <input
            type="file"
            accept=".xlsx,.xls"
            onChange={async event => {
              const file =
                event.target.files?.[0];

              if (file) {
                await handleFleetFile(file);
              }

              event.target.value = '';
            }}
            disabled={
              isReadOnly || hasFleetImport
            }
            id="fleet-upload"
            style={{ display: 'none' }}
          />

          <sdx-button
            theme="secondary"
            label="Excel-Template herunterladen"
            onClick={
              downloadFleetExcelTemplate
            }
            disabled={isReadOnly || undefined}
          ></sdx-button>

          {!hasFleetImport && (
            <label
              htmlFor="fleet-upload"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 14px',
                marginLeft: 8,
                border:
                  `1px dashed ${INTERACTION}`,
                borderRadius: 8,
                cursor: isReadOnly
                  ? 'default'
                  : 'pointer',
                fontSize: 14,
                color: INTERACTION,
                background: 'white',
              }}
            >
              📁 Datei auswählen
              (xlsx / xls)…
            </label>
          )}

          {formData.fleetImportMessage && (
            <Hint type="success">
              {formData.fleetImportMessage}{' '}
              Bitte die übernommenen Werte
              prüfen.
            </Hint>
          )}

          {hasFleetImport && (
            <div
              style={{
                marginTop: 12,
                padding: 12,
                border:
                  `1px solid ${GREEN_BORDER}`,
                borderRadius: 8,
                background: GREEN_SURFACE,
              }}
            >
              <div
                style={{
                  marginBottom: 8,
                  fontSize: 14,
                  fontWeight: 600,
                  color: BRAND,
                }}
              >
                Importierte Fahrzeuge
              </div>

              {importedVehicles.map(
                (vehicle, index) => (
                  <div
                    key={`${vehicle.driveType}-${index}`}
                    style={{
                      display: 'flex',
                      justifyContent:
                        'space-between',
                      gap: 16,
                      padding: '6px 0',
                      borderBottom:
                        index <
                        importedVehicles.length -
                          1
                          ? `1px solid ${GREEN_BORDER}`
                          : 'none',
                      fontSize: 13,
                      color: '#334155',
                    }}
                  >
                    <span>
                      {vehicle.driveType ===
                      'combustion'
                        ? 'Verbrenner'
                        : vehicle.driveType ===
                          'electric'
                        ? 'Elektro'
                        : 'Hybrid'}
                    </span>

                    <span>
                      {vehicle.age} Jahre,{' '}
                      {vehicle.mileage.toLocaleString(
                        'de-CH'
                      )}{' '}
                      km
                    </span>
                  </div>
                )
              )}
            </div>
          )}

          {hasFleetImport && (
            <div style={{ marginTop: 12 }}>
              <button
                type="button"
                onClick={removeFleetImport}
                disabled={isReadOnly}
                style={{
                  background: '#fff1f2',
                  color: '#b91c1c',
                  border:
                    '1px solid #fecaca',
                  borderRadius: 8,
                  padding: '10px 14px',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: isReadOnly
                    ? 'default'
                    : 'pointer',
                  fontFamily: 'inherit',
                  opacity: isReadOnly
                    ? 0.6
                    : 1,
                }}
              >
                Importierte Datei entfernen
              </button>
            </div>
          )}

          {formData.fleetImportError && (
            <Hint type="warning">
              {formData.fleetImportError}
            </Hint>
          )}
        </div>

        <div
          style={{
            borderTop:
              '1px solid #e5e7eb',
            paddingTop: 16,
            opacity: hasFleetImport
              ? 0.45
              : 1,
          }}
        >
          <FleetRow
            title="Verbrenner"
            countField="combustionCount"
            ageField="combustionAvgAge"
            kmField="combustionAvgKm"
            formData={formData}
            setField={setField}
            disabled={
              manualFieldsDisabled
            }
            fieldErrors={fieldErrors}
          />

          <FleetRow
            title="Elektro"
            countField="electricCount"
            ageField="electricAvgAge"
            kmField="electricAvgKm"
            formData={formData}
            setField={setField}
            disabled={
              manualFieldsDisabled
            }
            fieldErrors={fieldErrors}
          />

          <FleetRow
            title="Hybrid"
            countField="hybridCount"
            ageField="hybridAvgAge"
            kmField="hybridAvgKm"
            formData={formData}
            setField={setField}
            disabled={
              manualFieldsDisabled
            }
            fieldErrors={fieldErrors}
          />
        </div>

        {total > 0 && (
          <div
            style={{
              display: 'flex',
              gap: 16,
              padding: 14,
              background: '#f8fafc',
              borderRadius: 8,
              border:
                '1px solid #e2e8f0',
              marginTop: 8,
              marginBottom: 16,
            }}
          >
            <div style={{ flex: 1 }}>
              <div
                style={{
                  fontSize: 12,
                  color: MUTED,
                }}
              >
                Flottengrösse
              </div>

              <div
                style={{
                  fontSize: 22,
                  fontWeight: 700,
                  color: '#1e293b',
                }}
              >
                {total}
              </div>
            </div>

            <div style={{ flex: 1 }}>
              <div
                style={{
                  fontSize: 12,
                  color: MUTED,
                }}
              >
                Elektrifizierungsgrad
              </div>

              <div
                style={{
                  fontSize: 22,
                  fontWeight: 700,
                  color:
                    INTERACTION,
                }}
              >
                {electrifiedShare}%
              </div>
            </div>
          </div>
        )}

        <FieldLabel optional>Eigentumsverhältnis</FieldLabel>

        <div
          style={{
            display: 'flex',
            gap: 10,
            flexWrap: 'wrap',
          }}
        >
          {OWNERSHIP_OPTIONS.map(
            option => (
              <ChoiceCard
                key={option.value}
                label={option.label}
                description={option.scope}
                selected={
                  formData.fleetOwnership ===
                  option.value
                }
                onClick={() =>
                  setField(
                    'fleetOwnership',
                    formData.fleetOwnership ===
                      option.value
                      ? ''
                      : option.value
                  )
                }
                disabled={isReadOnly}
              />
            )
          )}
        </div>
      </SectionCard>

      {/* Entsorgung */}
      <SectionCard
        title="Entsorgung"
        badge="optional"
      >

        {/*
        <label
          className="label"
          style={{
            display: 'block',
            marginBottom: 8,
          }}
        >
          Welche Abfälle fallen im Betrieb
          an?
        </label>
        */}

{/*
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          {WASTE_OPTIONS.map(option => (
            <label
              key={option.value}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                cursor: isReadOnly
                  ? 'default'
                  : 'pointer',
              }}
            >
              <input
                type="checkbox"
                checked={(
                  formData.wasteCategories ||
                  []
                ).includes(option.value)}
                onChange={() =>
                  toggleWasteCategory(
                    option.value
                  )
                }
                disabled={isReadOnly}
                style={{
                  width: 16,
                  height: 16,
                  accentColor:
                    INTERACTION,
                }}
              />

              <span
                style={{
                  fontSize: 14,
                  color: '#1e293b',
                }}
              >
                {option.label}
              </span>
            </label>
          ))}
        </div>

*/}

        <div style={{ marginTop: 18 }}>
          <label
            className="label"
            style={{
              display: 'block',
              marginBottom: 8,
            }}
          >
            Entsorgung nicht reparierbarer
            Carrosserieteile
          </label>

          <div
            style={{
              display: 'flex',
              justifyContent:
                'space-between',
              marginBottom: 6,
              fontSize: 12,
              color: MUTED,
            }}
          >
            <span>Gemischte Mulde</span>
            <span>
              Abnahme durch Dritte
            </span>
          </div>

          <input
            type="range"
            min={0}
            max={100}
            step={10}
            value={
              bodyPartsThirdPartyShare
            }
            onChange={event =>
              setField(
                'bodyPartsThirdPartyShare',
                Number(
                  event.target.value
                )
              )
            }
            disabled={isReadOnly}
            style={{
              width: '100%',
              accentColor:
                INTERACTION,
              cursor: isReadOnly
                ? 'default'
                : 'pointer',
            }}
          />

          <div
            style={{
              display: 'flex',
              justifyContent:
                'space-between',
              marginTop: 8,
              fontSize: 13,
              color: '#475569',
            }}
          >
            <span>
              {bodyPartsMixedContainerShare}{' '}
              %
            </span>

            <span>
              {bodyPartsThirdPartyShare} %
            </span>
          </div>
        </div>
      </SectionCard>

      {/* Druckluft */}
      <SectionCard
        title="Druckluft & Kompressor"
        badge="optional"
      >
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            cursor: isReadOnly
              ? 'default'
              : 'pointer',
          }}
        >
          <input
            type="checkbox"
            checked={Boolean(
              formData.hasCompressedAir
            )}
            onChange={event =>
              setField(
                'hasCompressedAir',
                event.target.checked
              )
            }
            disabled={isReadOnly}
            style={{
              width: 16,
              height: 16,
              accentColor:
                INTERACTION,
            }}
          />

          <span
            style={{
              fontSize: 14,
              color: '#1e293b',
            }}
          >
            Druckluft / Kompressor im Einsatz
          </span>
        </label>

        {formData.hasCompressedAir && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns:
                '1fr 1fr',
              gap: 16,
              marginTop: 18,
            }}
          >
            
            <div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: 8,
                }}
              >
                <span
                  className="label"
                  style={{
                    display: 'block',
                    color: '#0f172a',
                  }}
                >
                  Kompressor-Typ
                </span>
                <RequiredBadge />
              </div>
              <sdx-select
                label=""
                placeholder="Auswählen…"
                value={formData.compressorType || ''}
                onInput={event =>
                  setField('compressorType', readSdxValue(event))
                }
                disabled={isReadOnly || undefined}
                valid={
                  fieldErrors?.compressorType
                    ? 'false'
                    : undefined
                }
                validation-message={
                  fieldErrors?.compressorType ||
                  undefined
                }
              >
              {COMPRESSOR_OPTIONS.map(type => (
                <sdx-select-option
                  key={type}
                  value={type}
                  selected={formData.compressorType === type}
                >
                  {type}
                </sdx-select-option>
              ))}
            </sdx-select>
            </div>

            <div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: 8,
                }}
              >
                <span
                  className="label"
                  style={{
                    display: 'block',
                    color: '#0f172a',
                  }}
                >
                  Baujahr
                </span>
                <RequiredBadge />
              </div>
              <sdx-input
                label=""
                type="number"
                value={
                  formData.compressorBuildYear ??
                  ''
                }
                onInput={event =>
                  setField(
                    'compressorBuildYear',
                    event.target.value
                  )
                }
                disabled={
                  isReadOnly || undefined
                }
                placeholder="z. B. 2018"
                valid={
                  fieldErrors?.compressorBuildYear
                    ? 'false'
                    : undefined
                }
                validation-message={
                  fieldErrors?.compressorBuildYear ||
                  undefined
                }
              ></sdx-input>
            </div>

            <div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: 8,
                }}
              >
                <span
                  className="label"
                  style={{
                    display: 'block',
                    color: '#0f172a',
                  }}
                >
                  Netzdruck (bar)
                </span>
                <RequiredBadge />
              </div>
              <sdx-input
                label=""
                type="text"
                inputmode="decimal"
                value={formatNumericInput(formData.compressedAirPressureBar ?? '')}
                onInput={event =>
                  setField(
                    'compressedAirPressureBar',
                    cleanNumericInput(event.target.value)
                  )
                }
                disabled={
                  isReadOnly || undefined
                }
                placeholder="z. B. 8"
                valid={
                  fieldErrors?.compressedAirPressureBar
                    ? 'false'
                    : undefined
                }
                validation-message={
                  fieldErrors?.compressedAirPressureBar ||
                  undefined
                }
              ></sdx-input>
            </div>

            <div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: 8,
                }}
              >
                <label
                  className="label"
                  style={{
                    display: 'block',
                  }}
                >
                  Abwärmenutzung
                </label>
                <RequiredBadge />
              </div>

              <div
                style={{
                  display: 'flex',
                  gap: 14,
                  flexWrap: 'wrap',
                }}
              >
                {[
                  {
                    value: 'yes',
                    label: 'Ja',
                  },
                  {
                    value: 'no',
                    label: 'Nein',
                  },
                  {
                    value: 'unknown',
                    label: 'Weiss nicht',
                  },
                ].map(option => (
                  <label
                    key={option.value}
                    style={{
                      display: 'flex',
                      alignItems:
                        'center',
                      gap: 6,
                      cursor: isReadOnly
                        ? 'default'
                        : 'pointer',
                    }}
                  >
                    <input
                      type="radio"
                      name="compressorHeatRecovery"
                      value={option.value}
                      checked={
                        formData.compressorHeatRecovery ===
                        option.value
                      }
                      onChange={event =>
                        setField(
                          'compressorHeatRecovery',
                          event.target
                            .value
                        )
                      }
                      disabled={isReadOnly}
                      style={{
                        accentColor: INTERACTION,
                      }}
                    />

                    <span>
                      {option.label}
                    </span>
                  </label>
                ))}
              </div>
              {fieldErrors?.compressorHeatRecovery && (
                <span style={ERROR_TEXT}>
                  {fieldErrors.compressorHeatRecovery}
                </span>
              )}
            </div>

            <div
              style={{
                gridColumn: '1 / -1',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: 8,
                }}
              >
                <label
                  className="label"
                  style={{
                    display: 'block',
                  }}
                >
                  Sind Leckagen im
                  Druckluftnetz bekannt?
                </label>
                <RequiredBadge />
              </div>

              <div
                style={{
                  display: 'flex',
                  gap: 14,
                  flexWrap: 'wrap',
                }}
              >
                {[
                  {
                    value: 'yes',
                    label: 'Ja',
                  },
                  {
                    value: 'no',
                    label: 'Nein',
                  },
                  {
                    value: 'unknown',
                    label: 'Weiss nicht',
                  },
                ].map(option => (
                  <label
                    key={option.value}
                    style={{
                      display: 'flex',
                      alignItems:
                        'center',
                      gap: 6,
                      cursor: isReadOnly
                        ? 'default'
                        : 'pointer',
                    }}
                  >
                    <input
                      type="radio"
                      name="compressedAirLeaks"
                      value={option.value}
                      checked={
                        formData.compressedAirLeaks ===
                        option.value
                      }
                      onChange={event =>
                        setField(
                          'compressedAirLeaks',
                          event.target
                            .value
                        )
                      }
                      disabled={isReadOnly}
                      style={{
                        accentColor: INTERACTION,
                      }}
                    />

                    <span>
                      {option.label}
                    </span>
                  </label>
                ))}
              </div>
              {fieldErrors?.compressedAirLeaks && (
                <span style={ERROR_TEXT}>
                  {fieldErrors.compressedAirLeaks}
                </span>
              )}
            </div>

            <div
              style={{
                gridColumn: '1 / -1',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: 8,
                }}
              >
                <label
                  className="label"
                  style={{
                    display: 'block',
                  }}
                >
                  Schleifmaschinen pneumatisch?
                </label>
                <RequiredBadge />
              </div>

              <div
                style={{
                  display: 'flex',
                  gap: 14,
                  flexWrap: 'wrap',
                }}
              >
                {[
                  {
                    value: 'yes',
                    label: 'Ja',
                  },
                  {
                    value: 'no',
                    label: 'Nein',
                  },
                  {
                    value: 'unknown',
                    label: 'Weiss nicht',
                  },
                ].map(option => (
                  <label
                    key={option.value}
                    style={{
                      display: 'flex',
                      alignItems:
                        'center',
                      gap: 6,
                      cursor: isReadOnly
                        ? 'default'
                        : 'pointer',
                    }}
                  >
                    <input
                      type="radio"
                      name="pneumaticGrinders"
                      value={option.value}
                      checked={
                        formData.pneumaticGrinders ===
                        option.value
                      }
                      onChange={event =>
                        setField(
                          'pneumaticGrinders',
                          event.target
                            .value
                        )
                      }
                      disabled={isReadOnly}
                      style={{
                        accentColor: INTERACTION,
                      }}
                    />

                    <span>
                      {option.label}
                    </span>
                  </label>
                ))}
              </div>
              {formData.pneumaticGrinders === 'no' && (
                <span
                  style={{
                    display: 'block',
                    marginTop: 6,
                    fontSize: 13,
                    color: '#64748b',
                  }}
                >
                  Annahme: Schleifmaschinen werden elektrisch betrieben.
                </span>
              )}
              {fieldErrors?.pneumaticGrinders && (
                <span style={ERROR_TEXT}>
                  {fieldErrors.pneumaticGrinders}
                </span>
              )}
            </div>
          </div>
        )}
      </SectionCard>

      {/* Lighting */}
      <SectionCard
        title="Beleuchtung & Infrastruktur"
        badge="optional"
      >
        <div
          style={{
            display: 'flex',
            gap: 10,
            flexWrap: 'wrap',
          }}
        >
          {[
            {
              value: 'led',
              label: 'LED',
              desc:
                'Vollständig umgestellt',
            },
            {
              value: 'partial_led',
              label: 'Teilweise LED',
              desc: 'Gemischter Bestand',
            },
            {
              value: 'conventional',
              label: 'Konventionell',
              desc:
                'Leuchtstoffröhren / Halogen',
            },
          ].map(option => (
            <ChoiceCard
              key={option.value}
              label={option.label}
              description={option.desc}
              selected={
                formData.lighting ===
                option.value
              }
              onClick={() =>
                setField(
                  'lighting',
                  formData.lighting ===
                    option.value
                    ? ''
                    : option.value
                )
              }
              disabled={isReadOnly}
            />
          ))}
        </div>

        {(formData.lighting ===
          'conventional' ||
          formData.lighting ===
            'partial_led') && (
          <Hint
            type={
              formData.lighting ===
              'conventional'
                ? 'warning'
                : 'info'
            }
          >
            Umstieg auf LED ist oft ein
            schneller, günstiger
            Effizienz-Hebel.
          </Hint>
        )}
      </SectionCard>
    </div>
  );
}
